import type { SignInResource, SignUpResource, SignedInSessionResource } from '@clerk/shared/types';

export type OAuthStrategy = 'oauth_apple' | 'oauth_google';

/** Options for starting an OAuth redirect flow */
export type AuthenticateWithRedirectOptions = {
  /** Use 'signUp' for new user registration, 'signIn' for existing users (default: 'signIn') */
  mode?: 'signIn' | 'signUp';
  /** URL to redirect to after OAuth provider callback (defaults to provider's callbackUrl) */
  redirectUrl?: string;
  /** Final URL after authentication completes (defaults to provider's callbackUrl) */
  redirectUrlComplete?: string;
  /** Skip signing out before starting OAuth (useful for signup flows) */
  skipSignOut?: boolean;
  /** The OAuth provider to use */
  strategy: OAuthStrategy;
};

// Missing fields types for SSO sign-up completion
export type MissingFieldsData = {
  firstName: string;
  lastName: string;
};

export type MissingFieldsParams = {
  completeSignUp: (data: MissingFieldsData) => Promise<void>;
  missingFields: string[];
};

/**
 * Check if an error is a user cancellation (from Clerk)
 */
export const isUserCancellationError = (error: unknown): boolean => {
  if (error === null || typeof error !== 'object') {
    return false;
  }

  const err = error as Record<string, unknown>;

  // Clerk cancellation - check message patterns
  if (typeof err.message === 'string') {
    const message = err.message.toLowerCase();
    if (message.includes('cancel') || message.includes('user closed') || message.includes('dismissed')) {
      return true;
    }
  }

  // Clerk API error with cancellation code
  if (Array.isArray(err.errors)) {
    const clerkErrors = err.errors as Array<{ code?: string; message?: string }>;
    return clerkErrors.some(
      (e) =>
        e.code === 'user_cancelled' ||
        e.message?.toLowerCase().includes('cancel') ||
        e.message?.toLowerCase().includes('dismissed'),
    );
  }

  return false;
};

export type SSOContext = {
  activeProvider: OAuthStrategy | null;
  destroyClient: () => Promise<void>;
  getToken: (template?: string) => Promise<string | null>;
  /** Complete the OAuth redirect callback and activate the session */
  handleRedirectCallback: () => Promise<void>;
  /** Transfer OAuth verification from signUp to signIn (for existing users) */
  handleTransferToSignIn: () => Promise<void>;
  /** Transfer OAuth verification from signIn to signUp (for new users) */
  handleTransferToSignUp: () => Promise<void>;
  isLoaded: boolean;
  isOAuthRedirecting: boolean;
  session?: SignedInSessionResource | null;
  setActive: (params: { session: string }) => Promise<void>;
  signIn: SignInResource | null;
  signOut: () => Promise<void>;
  signUp: SignUpResource | null;
  startOAuthFlow: (options: AuthenticateWithRedirectOptions) => Promise<void>;
  updateSignUp: (data: MissingFieldsData) => Promise<SignUpResource | undefined>;
};

// Re-export Clerk types for external use
export type { SignInResource, SignUpResource } from '@clerk/shared/types';
